import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { load as loadYaml } from 'js-yaml';
import { OkAction, ErrorAction } from './baseActions';
import { downloadService } from '../services/userAPI';
import { InstallerInfo, InstallerFile } from '../types/resources';
import { formatApiErrorPayload } from './utils';

// To avoid circular imports between modules, we must import only the AppState type here
type AppState = import('~/store').AppState;

export enum DownloadActionType {
  SILENT_GET_WORKER_DOWNLOADS = 'SILENT_GET_WORKER_DOWNLOADS',
  GET_WORKER_DOWNLOADS = 'GET_WORKER_DOWNLOADS',
  GET_WORKER_DOWNLOADS_OK = 'GET_WORKER_DOWNLOADS_OK',
  GET_WORKER_DOWNLOADS_ERROR = 'GET_WORKER_DOWNLOADS_ERROR',

  SILENT_GET_ASSISTANT_DOWNLOADS = 'SILENT_GET_ASSISTANT_DOWNLOADS',
  GET_ASSISTANT_DOWNLOADS = 'GET_ASSISTANT_DOWNLOADS',
  GET_ASSISTANT_DOWNLOADS_OK = 'GET_ASSISTANT_DOWNLOADS_OK',
  GET_ASSISTANT_DOWNLOADS_ERROR = 'GET_ASSISTANT_DOWNLOADS_ERROR',
}

type GetWorkerDownloadsAction =
  | Action<DownloadActionType.SILENT_GET_WORKER_DOWNLOADS>
  | Action<DownloadActionType.GET_WORKER_DOWNLOADS>
  | OkAction<DownloadActionType.GET_WORKER_DOWNLOADS_OK, InstallerInfo[]>
  | ErrorAction<DownloadActionType.GET_WORKER_DOWNLOADS_ERROR>;

type GetAssistantDownloadsAction =
  | Action<DownloadActionType.SILENT_GET_ASSISTANT_DOWNLOADS>
  | Action<DownloadActionType.GET_ASSISTANT_DOWNLOADS>
  | OkAction<DownloadActionType.GET_ASSISTANT_DOWNLOADS_OK, InstallerInfo[]>
  | ErrorAction<DownloadActionType.GET_ASSISTANT_DOWNLOADS_ERROR>;

export type DownloadAction = GetWorkerDownloadsAction | GetAssistantDownloadsAction;

export const getAssistantDownloads =
  (): ThunkAction<void, AppState, null, GetAssistantDownloadsAction> =>
  async (dispatch, getState) => {
    if (getState().userResources.assistantDownloads.allNames.length > 0) {
      dispatch({ type: DownloadActionType.SILENT_GET_ASSISTANT_DOWNLOADS });
    } else {
      dispatch({ type: DownloadActionType.GET_ASSISTANT_DOWNLOADS });
    }

    const installerData: InstallerInfo[] = [];

    try {
      // Get Mac installer
      const macResponse = await downloadService.getAssistantMac();

      if (macResponse.ok) {
        const data = macResponse.data ? (loadYaml(macResponse.data) as InstallerInfo) : undefined;
        if (data) {
          const installer = data.files.find((item: InstallerFile) => item.url.includes('dmg'));
          if (installer) {
            installerData.push({
              path: installer.url,
              sha512: data.sha512,
              version: data.version,
              releaseDate: data.releaseDate,
              files: [installer],
            });
          }
        }
      } else {
        dispatch({
          ...formatApiErrorPayload(macResponse),
          type: DownloadActionType.GET_ASSISTANT_DOWNLOADS_ERROR,
        });

        return;
      }

      // Get Windows installer
      const winResponse = await downloadService.getAssistantWin();

      if (winResponse.ok) {
        const data = winResponse.data ? (loadYaml(winResponse.data) as InstallerInfo) : undefined;
        if (data) {
          installerData.push(data);
        }
      } else {
        dispatch({
          ...formatApiErrorPayload(winResponse),
          type: DownloadActionType.GET_ASSISTANT_DOWNLOADS_ERROR,
        });

        return;
      }

      // Get Linux installer
      const linuxResponse = await downloadService.getAssistantLinux();

      if (linuxResponse.ok) {
        const data = linuxResponse.data
          ? (loadYaml(linuxResponse.data) as InstallerInfo)
          : undefined;
        if (data) {
          installerData.push(data);
        }
      } else {
        dispatch({
          ...formatApiErrorPayload(linuxResponse),
          type: DownloadActionType.GET_ASSISTANT_DOWNLOADS_ERROR,
        });

        return;
      }

      dispatch({
        type: DownloadActionType.GET_ASSISTANT_DOWNLOADS_OK,
        data: installerData,
      });
    } catch (error: unknown) {
      dispatch({
        type: DownloadActionType.GET_ASSISTANT_DOWNLOADS_ERROR,
        error: (error as Error).toString?.(),
      });
    }
  };
